import { DEFAULT_ACCENT_HEX } from '../../lib/brand';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BellOff, Bell, Loader2, Rss } from 'lucide-react';
import { AppCard } from '../../components/ui/AppCard';
import { AvatarView } from '../../components/ui/AvatarView';
import { Button } from '../../components/ui/Button';
import { EmptyState } from '../../components/ui/EmptyState';
import { ScreenHeader } from '../../components/ui/ScreenHeader';
import { fetchFollowingFeed, fetchMyChannelStats, muteChannel, unfollowChannel, unmuteChannel, type FollowingFeedItem } from '../../api/dataApi';
import { ClampedMarkdown } from '../../components/reports/MarkdownReportText';
import { ago, describeItem, groupFeed } from '../../lib/followingFeed';

const PAGE = 20;

// Updates from the public channels you follow: newest reports and recent
// signal activity, grouped by channel. Honest by design - there is no
// "since your last visit" because last-seen is not tracked.
export function FollowingScreen() {
  const [items, setItems] = useState<FollowingFeedItem[]>([]);
  const [followedCount, setFollowedCount] = useState<number | null>(null);
  const [cursor, setCursor] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unfollowed, setUnfollowed] = useState<Set<string>>(new Set());
  const [muted, setMuted] = useState<Record<string, boolean>>({});

  const load = useCallback(async (from: number) => {
    const page = await fetchFollowingFeed(PAGE, from);
    setItems((prev) => (from === 0 ? page.items : [...prev, ...page.items]));
    setFollowedCount(page.followedCount);
    setHasMore(page.hasMore);
    setCursor(page.nextCursor);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    load(0)
      .then(() => !cancelled && setError(null))
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : 'Unable to load your feed.'))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [load]);

  const groups = groupFeed(items).filter((g) => !unfollowed.has(g.channel));

  // Mute state is fetched per visible channel, once.
  useEffect(() => {
    for (const g of groups) {
      if (g.channel in muted) continue;
      setMuted((m) => ({ ...m, [g.channel]: false }));
      void fetchMyChannelStats(g.channel).then((s) => setMuted((m) => ({ ...m, [g.channel]: s.muted })));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  const toggleMute = async (channel: string) => {
    const next = !muted[channel];
    setMuted((m) => ({ ...m, [channel]: next }));
    try {
      await (next ? muteChannel(channel) : unmuteChannel(channel));
    } catch {
      setMuted((m) => ({ ...m, [channel]: !next }));
    }
  };

  const unfollow = async (channel: string) => {
    try {
      await unfollowChannel(channel);
      setUnfollowed((s) => new Set(s).add(channel));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unable to unfollow this channel.');
    }
  };

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      await load(cursor);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unable to load more.');
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <ScreenHeader title="Following" />
      {loading && (
        <div className="flex justify-center py-16" role="status" aria-label="Loading your feed">
          <Loader2 className="animate-spin text-black/40 dark:text-white/40" size={24} />
        </div>
      )}
      {error && (
        <div role="alert" className="text-sm text-red-600 dark:text-red-400">
          {error}{' '}
          <Button variant="plain" onClick={() => window.location.reload()}>
            Retry
          </Button>
        </div>
      )}
      {!loading && !error && followedCount === 0 && (
        <EmptyState icon={Rss} title="You're not following any channels yet" subtitle="Follow a public channel and its new reports and activity will show up here." />
      )}
      {!loading && !error && (followedCount ?? 0) > 0 && groups.length === 0 && (
        <EmptyState icon={Rss} title="Nothing new yet" subtitle="The channels you follow have no reports or recent signals to show." />
      )}
      {groups.map((g) => (
        <AppCard key={g.channel} className="flex flex-col gap-3">
          <div className="flex items-center gap-3">
            <AvatarView symbol={g.avatarSymbol || 'hash'} colorHex={g.accentHex || DEFAULT_ACCENT_HEX} imageKey={g.avatarImageKey} size={36} zoomable />
            <Link to={`/public/${encodeURIComponent(g.channel)}`} className="font-semibold truncate flex-1 no-underline text-inherit">
              {g.channelTitle || g.channel}
            </Link>
            <button
              type="button"
              aria-label={muted[g.channel] ? `Unmute ${g.channelTitle}` : `Mute ${g.channelTitle}`}
              aria-pressed={!!muted[g.channel]}
              onClick={() => toggleMute(g.channel)}
              className="p-2 text-black/50 dark:text-white/50"
            >
              {muted[g.channel] ? <BellOff size={18} /> : <Bell size={18} />}
            </button>
            <Button variant="bordered" onClick={() => unfollow(g.channel)}>
              Unfollow
            </Button>
          </div>
          <ul className="m-0 p-0 list-none flex flex-col gap-2">
            {g.items.map((it) => (
              <li key={`${it.kind}-${it.number ?? it.signalType}`} className="text-sm flex gap-2">
                <span className="text-black/45 dark:text-white/45 tabular-nums shrink-0 w-16">{ago(it.at)}</span>
                {it.kind === 'report' ? (
                  <div className="min-w-0 flex-1">
                    <Link to={`/public/${encodeURIComponent(g.channel)}/reports/${it.number}`} className="text-inherit font-medium">
                      Report #{it.number}
                    </Link>
                    {it.excerpt && <ClampedMarkdown text={it.excerpt} lines={10} className="text-sm flex flex-col gap-1" />}
                  </div>
                ) : (
                  <span>{describeItem(it)}</span>
                )}
              </li>
            ))}
          </ul>
        </AppCard>
      ))}
      {hasMore && !loading && (
        <Button variant="bordered" onClick={loadMore} loading={loadingMore}>
          Load more
        </Button>
      )}
    </div>
  );
}
